'use client'

import { PUSD_CONTRACT_ADDRESS } from '@/constants/tokens'
import { authFetch } from '@/lib/auth/auth-fetch'
import { executeWithRelayerFallback } from '@/lib/relayer-fallback'
import { useWallet } from '@/providers/WalletContext'
import type {
	OperationType,
	RelayClient,
	SafeTransaction,
} from '@polymarket/builder-relayer-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type Address, type LocalAccount, encodeFunctionData } from 'viem'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WalletStatus = 'none' | 'active' | 'revoked' | 'loading' | 'error'

interface WalletStatusResponse {
	eoaAddress: string | null
	safeAddress: string | null
	balance: number
	status: 'none' | 'active' | 'revoked'
	walletType?: 'safe' | 'safe_broken' | 'deposit_wallet'
	walletDeployed?: boolean
	walletCreatedAt?: string | null
}

interface CreateWalletResponse {
	eoaAddress: string
	safeAddress: string
}

interface ExportKeyResponse {
	privateKey: string
}

interface FundWithdrawResponse {
	success: boolean
	txHash?: string
}

interface DeployWalletResponse {
	success: boolean
	walletAddress?: string
	txHash?: string
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

async function fetchWalletStatus(): Promise<WalletStatusResponse> {
	const res = await authFetch('/api/copy-trading/wallet', {
		headers: { 'Content-Type': 'application/json' },
	})
	if (!res.ok) {
		throw new Error('Failed to fetch wallet status')
	}
	return res.json()
}

async function createWalletApi(): Promise<CreateWalletResponse> {
	const res = await authFetch('/api/copy-trading/wallet', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
	})
	if (!res.ok) {
		const data = await res.json().catch(() => ({}))
		throw new Error(data.error || 'Failed to create wallet')
	}
	return res.json()
}

/**
 * Request a short-lived intent challenge from the server, then sign the
 * returned message with the embedded wallet (Privy viem account). Produces the
 * three-part payload consumed by sensitive mutation endpoints (export,
 * withdraw).
 *
 * The user sees a wallet popup with the action's prefix ("Export CT private
 * key…"), so a leaked access token alone cannot trigger the mutation — an
 * attacker would need to trick the user into signing the specific message. The
 * signature recovers to the user's EOA, which is what the server matches
 * against (see lib/auth/intent-signature.ts).
 */
async function signIntent(
	account: LocalAccount,
	action: 'export-ct-key' | 'withdraw-ct',
	extra?: { amount?: string }
): Promise<{ message: string; signature: string; nonce: string }> {
	const chRes = await authFetch('/api/copy-trading/challenge', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ action, ...(extra ?? {}) }),
	})
	if (!chRes.ok) {
		const data = await chRes.json().catch(() => ({}))
		throw new Error(data.error || 'Failed to request authorization')
	}
	const { message, nonce } = (await chRes.json()) as { message: string; nonce: string }

	const signature = await account.signMessage({ message })
	return { message, signature, nonce }
}

async function exportKeyApi(account: LocalAccount): Promise<ExportKeyResponse> {
	const intent = await signIntent(account, 'export-ct-key')
	const res = await authFetch('/api/copy-trading/wallet/export', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(intent),
	})
	if (!res.ok) {
		const data = await res.json().catch(() => ({}))
		throw new Error(data.error || 'Failed to export key')
	}
	return res.json()
}

async function withdrawWalletApi(
	amount: string,
	account: LocalAccount
): Promise<FundWithdrawResponse> {
	const intent = await signIntent(account, 'withdraw-ct', { amount })
	const res = await authFetch('/api/copy-trading/wallet/withdraw', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(intent),
	})
	const data = await res.json().catch(() => ({}))
	// 202 = Safe deploy still in progress server-side, withdraw did not execute
	if (res.status === 202) {
		throw new Error(data.error || 'Wallet activation in progress. Please retry in 30 seconds.')
	}
	if (!res.ok) {
		throw new Error(data.error || 'Failed to withdraw')
	}
	return data
}

async function deployWalletApi(): Promise<DeployWalletResponse> {
	const res = await authFetch('/api/copy-trading/wallet/deploy', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
	})
	const data = await res.json().catch(() => ({}))
	if (!res.ok) {
		throw new Error(data.error || 'Failed to deploy wallet')
	}
	return data
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Get wallet status and balance. Polls every 30s.
 */
export function useWalletStatus() {
	const { authenticated } = useWallet()

	const query = useQuery({
		queryKey: ['copy-trading-wallet'],
		queryFn: async () => {
			return fetchWalletStatus()
		},
		enabled: authenticated,
		refetchInterval: 30_000,
		staleTime: 10_000,
		gcTime: 5 * 60_000, // keep cache longer to avoid flicker
		retry: 1,
		placeholderData: prev => prev, // keep old data while refetching
	})

	const walletStatus: WalletStatus =
		query.isLoading || (!authenticated && !query.data)
			? 'loading'
			: query.isError
				? 'error'
				: (query.data?.status ?? 'none')

	return {
		eoaAddress: query.data?.eoaAddress ?? null,
		safeAddress: query.data?.safeAddress ?? null,
		balance: Number(query.data?.balance) || 0,
		status: walletStatus,
		walletType: query.data?.walletType,
		walletDeployed: query.data?.walletDeployed ?? true,
		walletCreatedAt: query.data?.walletCreatedAt ?? null,
		isLoading: query.isLoading,
		refetch: query.refetch,
	}
}

/**
 * Create a new dedicated copy trading wallet.
 */
export function useCreateWallet() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async () => {
			return createWalletApi()
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
		},
	})
}

/**
 * Deploy the server-owned copy-trading deposit wallet without submitting a trade.
 */
export function useDeployCopyTradingWallet() {
	const queryClient = useQueryClient()
	const { authenticated } = useWallet()

	return useMutation({
		mutationFn: async () => {
			if (!authenticated) throw new Error('Not signed in')
			return deployWalletApi()
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			}, 5000)
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			}, 15000)
		},
	})
}

/**
 * Export the private key on demand.
 *
 * Requires the user to sign a message with prefix "Export CT private key".
 * See lib/auth/intent-signature.ts for the threat model.
 */
export function useExportKey() {
	const { evmAccount } = useWallet()

	return useMutation({
		mutationFn: async () => {
			if (!evmAccount) throw new Error('Wallet not ready')
			return exportKeyApi(evmAccount)
		},
	})
}

/**
 * Fund the copy trading wallet by transferring pUSD from the user's main
 * Polymarket wallet: legacy Safe for existing users, deposit wallet for new
 * post-V2 users.
 */
export function useFundWallet(relayClient: RelayClient | null, sourceSafeAddress?: string) {
	const queryClient = useQueryClient()
	const { walletClient, eoaAddress, publicClient, polymarketSafe } = useWallet()

	return useMutation({
		mutationFn: async (amount: string) => {
			if (!(walletClient && eoaAddress)) {
				throw new Error('Wallet not connected')
			}

			// Fetch copy trading wallet Safe address with auth
			const walletRes = await fetchWalletStatus()
			const targetSafe = walletRes?.safeAddress
			if (!targetSafe) throw new Error('Copy trading wallet not found')

			const pusdAmount = BigInt(Math.round(Number.parseFloat(amount) * 1e6))
			if (pusdAmount <= 0n) throw new Error('Amount must be positive')

			const { createPublicClient, http, erc20Abi } = await import('viem')
			const { polygon } = await import('viem/chains')
			const pub = publicClient ?? createPublicClient({ chain: polygon, transport: http() })
			const mainSafe = (sourceSafeAddress ?? polymarketSafe) as `0x${string}` | undefined

			// New Polymarket users fund from their main deposit wallet. The old
			// Safe transfer path reads a different address and reports $0 even
			// when the UI correctly shows pUSD on the deposit wallet.
			if (!mainSafe) {
				const {
					buildErc20TransferCall,
					deriveDepositWalletAddress,
					executeDepositWalletBatch,
					ensureDepositWalletReady,
				} = await import('@/lib/wallets/polymarket-deposit')
				const sourceDepositWallet = deriveDepositWalletAddress(eoaAddress as Address)

				const balance = await pub.readContract({
					address: PUSD_CONTRACT_ADDRESS as `0x${string}`,
					abi: erc20Abi,
					functionName: 'balanceOf',
					args: [sourceDepositWallet],
				})
				if (balance < pusdAmount) {
					throw new Error(`Insufficient balance: $${(Number(balance) / 1e6).toFixed(2)} available`)
				}

				if (!publicClient) throw new Error('Wallet RPC not ready')
				await ensureDepositWalletReady({
					walletClient,
					publicClient,
					depositWalletAddress: sourceDepositWallet,
				})
				await executeDepositWalletBatch(
					walletClient,
					[
						buildErc20TransferCall(
							PUSD_CONTRACT_ADDRESS as Address,
							targetSafe as Address,
							pusdAmount
						),
					],
					sourceDepositWallet
				)
				return
			}

			// Legacy Polymarket users fund from their main Safe.
			const balance = await pub.readContract({
				address: PUSD_CONTRACT_ADDRESS as `0x${string}`,
				abi: erc20Abi,
				functionName: 'balanceOf',
				args: [mainSafe],
			})
			if (balance < pusdAmount) {
				throw new Error(`Insufficient balance: $${(Number(balance) / 1e6).toFixed(2)} available`)
			}

			// Encode pUSD transfer: transfer(address to, uint256 amount)
			const transferData = encodeFunctionData({
				abi: [
					{
						name: 'transfer',
						type: 'function',
						inputs: [
							{ name: 'to', type: 'address' },
							{ name: 'amount', type: 'uint256' },
						],
						outputs: [{ name: '', type: 'bool' }],
					},
				],
				functionName: 'transfer',
				args: [targetSafe as `0x${string}`, pusdAmount],
			})

			const tx: SafeTransaction = {
				to: PUSD_CONTRACT_ADDRESS,
				value: '0',
				data: transferData,
				operation: 0 as OperationType,
			}

			// Create own RelayClient if not provided by TradingProvider
			let relay = relayClient
			if (!relay) {
				const { POLYGON_CHAIN_ID, RELAYER_FALLBACK_URL: RELAYER_URL } = await import(
					'@/constants/polymarket'
				)
				const { BuilderConfig } = await import('@polymarket/builder-signing-sdk')
				const { RelayClient: RC } = await import('@polymarket/builder-relayer-client')

				const signingUrl = `${window.location.origin}/api/polymarket/trading`
				const builderConfig = new BuilderConfig({
					remoteBuilderConfig: { url: signingUrl },
				})

				relay = new RC(
					RELAYER_URL,
					POLYGON_CHAIN_ID,
					// biome-ignore lint/suspicious/noExplicitAny: RelayClient typing
					walletClient as any,
					builderConfig
				)
			}

			await executeWithRelayerFallback(relay, walletClient, [tx], 'Fund copy trading wallet')
		},
		onSuccess: () => {
			// Invalidate immediately + delayed refetch (tx needs time to mine on Polygon)
			queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			}, 5000)
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			}, 15000)
		},
	})
}

/**
 * Withdraw from the copy trading wallet.
 *
 * Requires the user to sign a message with prefix "Withdraw from CT wallet"
 * that includes the exact withdraw amount — amount is server-bound via the
 * intent nonce, so an attacker cannot substitute a different amount after
 * the user approves.
 */
export function useWithdrawWallet() {
	const queryClient = useQueryClient()
	const { evmAccount } = useWallet()

	return useMutation({
		mutationFn: async (amount: string) => {
			if (!evmAccount) throw new Error('Wallet not ready')
			return withdrawWalletApi(amount, evmAccount)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			}, 5000)
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['copy-trading-wallet'] })
			}, 15000)
		},
	})
}
